import React, { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/coastal-dusk.css';

const VideoBreakPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);
  const [showVideoLightbox, setShowVideoLightbox] = useState(false);

  const handleSkip = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    continueToNextPrompt();
  };

  const continueToNextPrompt = () => {
    navigate('/journaling', { 
      state: { 
        responses: location.state?.responses || {},
        resumeAtPrompt: 4
      },
      replace: true
    });
  };

  const openVideoLightbox = () => {
    setShowVideoLightbox(true);
    document.body.style.overflow = 'hidden';
  };

  const closeVideoLightbox = () => {
    setShowVideoLightbox(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <div className="min-h-screen relative" style={{ background: 'var(--grey-deep)' }}>
      {/* Blurred Background Image */}
      <div className="landing-bg-image" style={{ backgroundImage: 'url(/assets/landing/bg-coastal.jpg)' }} />
      
      {/* Gradient Orbs */}
      <div className="landing-orb cool focus-shift" style={{ '--orb-opacity': '0.4', top: '15%', left: '-5%', width: '42vw', height: '42vw', maxWidth: '450px', maxHeight: '450px' }} />
      <div className="landing-orb neutral" style={{ '--orb-opacity': '0.3', top: '55%', right: '-5%', width: '38vw', height: '38vw', maxWidth: '400px', maxHeight: '400px', animationDelay: '-4s' }} />

      {/* Navigation Buttons */}
      <div className="absolute top-6 left-6 right-6 z-50 flex justify-between items-center">
        {/* Back Button */}
        <button
          onClick={() => {
            navigate('/journaling', { 
              state: { 
                responses: location.state?.responses || {},
                resumeAtPrompt: 3
              },
              replace: true
            });
          }}
          className="cta-pill-landing"
          style={{ fontSize: '0.78rem', padding: '8px 16px' }}
          data-testid="back-to-prompt-btn"
        >
          Back
        </button>
        
        {/* Next Button - Center */}
        <button
          onClick={handleSkip}
          className="cta-pill-landing"
          style={{ fontSize: '0.78rem', padding: '10px 24px' }}
          data-testid="next-video-btn"
        >
          Next
        </button>
        
        {/* Logout Button */}
        <button
          onClick={() => {
            localStorage.removeItem('token');
            navigate('/');
          }}
          className="cta-pill-landing"
          style={{ fontSize: '0.78rem', padding: '8px 16px' }}
          title="Your progress is automatically saved"
        >
          Logout
        </button>
      </div>

      {/* Main Content */}
      <div className="min-h-screen flex flex-col items-center justify-center px-6 relative z-10">
        {/* Title — editorial style */}
        <div className="editorial-label headline-focus-in mb-6" style={{ letterSpacing: '0.22em' }}>MINI BREAK</div>
        <h1 style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: 'clamp(1.1rem, 3.5vw, 1.5rem)', color: 'var(--text-primary)', letterSpacing: '-0.01em', textAlign: 'center', marginBottom: '2rem', lineHeight: 1.2 }} className="headline-focus-in-d1">
          A moment<br />to breathe
        </h1>

        {/* Video Thumbnail */}
        <div 
          className="landing-video-preview mx-auto"
          style={{ 
            maxHeight: '65vh',
            width: 'auto',
            height: '55vh',
            aspectRatio: '9/16'
          }}
          onClick={openVideoLightbox}
          data-testid="mini-break-video-thumbnail"
        >
          <img 
            src="https://img.youtube.com/vi/0dl2aXrJnIk/maxresdefault.jpg"
            alt="Mini Break Video"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = "https://img.youtube.com/vi/0dl2aXrJnIk/hqdefault.jpg"; }}
          />
          <div className="play-overlay">
            <div className="play-circle">
              <svg viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" fill="var(--text-primary)"/>
              </svg>
            </div>
          </div>
        </div>

        <p className="text-center mt-6 headline-focus-in-d2" style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 300, fontSize: '0.88rem', color: 'rgba(163, 163, 163, 0.5)' }}>
          Take a mini break, or tap Next to continue
        </p>
      </div>

      {/* Video Lightbox — with clear exit */}
      {showVideoLightbox && (
        <div 
          className="lightbox-overlay"
          onClick={closeVideoLightbox}
          data-testid="video-lightbox-overlay"
          style={{ zIndex: 9999 }}
        >
          {/* Close button — large and visible */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeVideoLightbox();
            }}
            data-testid="video-lightbox-close"
            style={{ 
              position: 'fixed', top: '16px', right: '16px', zIndex: 10001, 
              width: '48px', height: '48px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.95)', color: '#1a1a1a',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: 'none', cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
            }}
          >
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div 
            className="w-full h-full max-w-md mx-4 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="w-full relative rounded-2xl overflow-hidden" 
              style={{ aspectRatio: '9/16', maxHeight: 'calc(100vh - 120px)' }}
            >
              <iframe
                src="https://www.youtube.com/embed/0dl2aXrJnIk?autoplay=1&rel=0&modestbranding=1&showinfo=0&iv_load_policy=3"
                title="Mini Break Video"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
              />
            </div>
          </div>
          
          {/* Bottom tap-to-close */}
          <p 
            onClick={(e) => { e.stopPropagation(); closeVideoLightbox(); }}
            style={{ 
              position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
              fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.82rem', 
              color: 'rgba(255,255,255,0.6)', cursor: 'pointer', zIndex: 10001,
              padding: '10px 24px', borderRadius: '9999px',
              background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)'
            }}
          >
            Tap here or &times; to close
          </p>
        </div>
      )}
    </div>
  );
};

export default VideoBreakPage;
